import React, { useCallback } from 'react';
import { useThemeStore } from '../../state/theme';
import { CloudUpload, FileText, Loader2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface FileUploadProps {
  title?: string;
  files: File[];
  onFilesChange: (files: File[]) => void;
  required?: boolean;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  maxFiles?: number;
  error?: string;
  fieldName?: string; // for FormData field name
  isUploading?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'compact'; // 'compact' renders a smaller dropzone
}

const FileUpload: React.FC<FileUploadProps> = ({
  title,
  files,
  onFilesChange,
  required = false,
  accept = '.pdf,.doc,.docx',
  multiple = true,
  maxSize = 5,
  maxFiles = 10,
  error,
  fieldName = 'resources',
  isUploading = false,
  disabled = false,
  variant = 'default',
}) => {
  const { isDarkMode } = useThemeStore();
  const isCompact = variant === 'compact';

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const selectedFiles = Array.from(event.target.files || []);
      if (selectedFiles.length === 0) return;

      const allFiles = [...files, ...selectedFiles];

      if (allFiles.length > maxFiles) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }

      for (const file of selectedFiles) {
        if (file.size > maxSize * 1024 * 1024) {
          toast.error(`File "${file.name}" exceeds ${maxSize}MB limit`);
          return;
        }
      }

      onFilesChange(allFiles);
    },
    [files, onFilesChange, maxFiles, maxSize, disabled]
  );

  const removeFile = useCallback(
    (index: number) => {
      if (disabled) return;
      const newFiles = files.filter((_, i) => i !== index);
      onFilesChange(newFiles);
    },
    [files, onFilesChange, disabled]
  );

  const inputId = `file-upload-${(title || fieldName).toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="relative w-full">
      {isUploading && (
        <div
          className={`absolute inset-0 z-10 flex justify-center items-center rounded-lg bg-black/20 backdrop-blur-sm max-h-full ${
            title ? 'top-8' : 'top-0'
          }`}
        >
          <Loader2 size={isCompact ? 20 : 28} className="animate-spin text-white" />
        </div>
      )}
      {title && (
        <p className="text-sm font-medium mb-3">
          {title}
          {required && <span className="text-red-500"> *</span>}
        </p>
      )}

      <input
        type="file"
        className="hidden"
        id={inputId}
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
      />

      <label
        htmlFor={inputId}
        className={`w-full border-2 border-dashed cursor-pointer transition ${
          isCompact
            ? 'flex flex-row justify-center items-center gap-2 min-h-[52px] px-4 py-2.5 rounded-lg'
            : 'flex flex-col justify-center items-center gap-2 min-h-[120px] p-6 text-center rounded-xl'
        } ${
          isDarkMode
            ? 'bg-gray-800 border-gray-600 hover:border-indigo-500 hover:bg-gray-700'
            : 'bg-white border-gray-400 hover:border-[#085454] hover:bg-[#f4fdfd]'
        } ${disabled || isUploading ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        {isCompact ? (
          <div className="flex flex-row items-center gap-2">
            <CloudUpload size={18} className="text-greySeventy" />
            <span
              className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}
            >
              {files.length > 0
                ? `${files.length} file(s) selected`
                : 'Click or drag to upload'}
            </span>
            <span className="text-[#085454] font-medium text-xs">
              Browse
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <CloudUpload size={38} className="text-greySeventy" />
            <span
              className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}
            >
              {files.length > 0
                ? `${files.length} file(s) selected`
                : 'Click or drag files here to upload'}
            </span>
            <span className="text-[#085454] font-medium text-xs">
              Browse Files
            </span>
          </div>
        )}
      </label>

      {files.length > 0 && (
        <div className="mt-4">
          <h4
            className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Selected Files:
          </h4>
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li
                key={index}
                className={`flex items-center justify-between p-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
              >
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-indigo-600" />
                  <span
                    className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    {file.name?.slice(0, 20)}...
                  </span>
                  <span
                    className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                  disabled={disabled || isUploading}
                >
                  <X size={16} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
    </div>
  );
};

export default FileUpload;
